import axios, { AxiosInstance } from 'axios'
import config from '../config'

// Types
export interface Upload {
  id: number
  uploaded_at: string
  when_generated: string | null
  statement_date: string | null  // Period field from CSV - used for ordering
  filename: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  error_message: string | null
}

export interface UploadDetail extends Upload {
  account_count: number
  nav_row_count: number
  cash_row_count: number
  position_count: number
}

export interface Account {
  id: number
  account_number: string
  account_name: string | null
  display_order: number
  exclude_from_aggregate: boolean
  can_go_short: boolean
  min_cash_pct: number | null  // Minimum cash % to maintain for buy orders
  created_at: string
  updated_at: string
}

export interface NetAssetValue {
  id: number
  account_id: number
  asset_class: string
  prior_total: number | null
  current_long: number | null
  current_short: number | null
  current_total: number | null
  change: number | null
  row_type: string
  calculated_total_usd: number | null
}

export interface CashReport {
  id: number
  account_id: number
  line_item: string
  currency_summary: string | null
  currency: string | null
  total: number | null
  securities: number | null
  futures: number | null
  month_to_date: number | null
  year_to_date: number | null
  row_type: string
}

export interface OpenPosition {
  id: number
  account_id: number
  account_number: string | null
  data_discriminator: string | null
  asset_category: string
  currency: string | null
  symbol: string | null
  description: string | null
  quantity: number | null
  multiplier: number | null
  close_price: number | null
  cost_basis: number | null
  value: number | null
  unrealized_pnl: number | null
  code: string | null
  row_type: string
  nominal_exposure: number | null
  delta_adjusted_exposure: number | null
  implied_volatility: number | null
  exposure_calc_timestamp: string | null
  position_pct: number | null
  nominal_exposure_pct: number | null
  delta_exposure_pct: number | null
  nc_exposure: number | null
  nc_exposure_pct: number | null
  nc_exposure_error: string | null
}

export interface Portfolio {
  upload_id: number
  account_id: number | null
  account_number: string | null
  net_asset_values: NetAssetValue[]
  cash_reports: CashReport[]
  open_positions: OpenPosition[]
}

export interface AggregatedPosition {
  symbol: string | null
  description: string | null
  asset_category: string
  currency: string | null
  total_quantity: number
  total_value: number
  total_cost_basis: number | null
  total_unrealized_pnl: number | null
  close_price: number | null
  close_price_mismatch: boolean
  accounts: number[]
  total_nominal_exposure: number | null
  total_delta_adjusted_exposure: number | null
  implied_volatility: number | null
  total_position_pct: number | null
  total_nominal_exposure_pct: number | null
  total_delta_exposure_pct: number | null
  total_nc_exposure: number | null
  total_nc_exposure_pct: number | null
  has_nc_exposure_error: boolean
}

export interface AggregatedNav {
  asset_class: string
  total_prior: number | null
  total_current_long: number | null
  total_current_short: number | null
  total_current: number | null
  total_change: number | null
}

export interface Aggregate {
  upload_id: number
  included_accounts: number[]
  excluded_accounts: number[]
  nav_summary: AggregatedNav[]
  cash_summary: { line_item: string; currency: string | null; total: number | null }[]
  positions: AggregatedPosition[]
  total_portfolio_value: number
  calculated_total_usd: number | null
}

export interface AllocationItem {
  category: string
  value: number
  percentage: number
}

export interface Metrics {
  upload_id: number
  total_portfolio_value: number
  total_cash: number
  total_securities_value: number
  asset_allocation: AllocationItem[]
  currency_allocation: AllocationItem[]
  top_positions: {
    symbol: string
    description: string
    asset_category: string
    quantity: number
    value: number
    unrealized_pnl: number | null
  }[]
  custom_metrics: { name: string; value: number | null; category: string | null }[]
}

export interface FxRate {
  id: number
  base_currency: string
  quote_currency: string
  rate: number
  rate_date: string
  source: string | null
  created_at: string
}

export interface FxRateSummary {
  base_currency: string
  quote_currency: string
  rate: number
  rate_date: string
  source: string | null
}

export interface Option {
  occ_symbol: string
  symbol: string
  underlying: string
  expiration: string
  strike_price: number
  call_put: 'C' | 'P'
  currency: string | null
  price: number | null
  underlying_price: number | null
  delta: number | null
  implied_volatility: number | null
  dividend_yield: number | null
  iv_error: string | null  // Error code if IV calculation failed: 'below_intrinsic', 'no_converge', etc.
  delta_estimated: boolean  // True if delta was calculated using fallback method
  created_at: string
  updated_at: string
  price_timestamp: string | null
  underlying_price_timestamp: string | null
  delta_iv_timestamp: string | null
}

export interface BenchmarkRate {
  id: number
  duration: string
  duration_days: number
  rate: number
  rate_date: string
  source: string | null
  created_at: string
  updated_at: string
}

export interface OptionRefreshResult {
  options_extracted: number
  prices_updated: number
  greeks_calculated: number
  exposures_updated: number
  nc_exposures_updated: number
  accounts_updated: number
  percentages_updated: number
}

export interface SubsectorUnderlying {
  id: number
  underlying: string
  created_at: string
}

export interface Subsector {
  id: number
  name: string
  created_at: string
  updated_at: string
  underlyings: SubsectorUnderlying[]
}

// API client
class ApiClient {
  private client: AxiosInstance
  private getToken: (() => Promise<string | null>) | null = null

  constructor() {
    this.client = axios.create({
      baseURL: config.apiBaseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Add auth interceptor
    this.client.interceptors.request.use(async (config) => {
      if (this.getToken) {
        const token = await this.getToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      }
      return config
    })
  }

  setTokenGetter(getter: () => Promise<string | null>) {
    this.getToken = getter
  }

  // Uploads
  async uploadFile(file: File): Promise<Upload> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await this.client.post<Upload>('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  async getUploads(skip = 0, limit = 50): Promise<{ uploads: Upload[]; total: number }> {
    const response = await this.client.get('/upload', { params: { skip, limit } })
    return response.data
  }

  async getSnapshots(skip = 0, limit = 50): Promise<{ uploads: Upload[]; total: number }> {
    const response = await this.client.get('/upload/snapshots', { params: { skip, limit } })
    return response.data
  }

  async getUpload(uploadId: number): Promise<UploadDetail> {
    const response = await this.client.get<UploadDetail>(`/upload/${uploadId}`)
    return response.data
  }

  async deleteUpload(uploadId: number): Promise<void> {
    await this.client.delete(`/upload/${uploadId}`)
  }

  // Accounts
  async getAccounts(): Promise<{ accounts: Account[]; total: number }> {
    const response = await this.client.get('/accounts')
    return response.data
  }

  async updateAccount(accountId: number, data: Partial<Account>): Promise<Account> {
    const response = await this.client.patch<Account>(`/accounts/${accountId}`, data)
    return response.data
  }

  async toggleAccountExclusion(accountId: number): Promise<Account> {
    const response = await this.client.patch<Account>(`/accounts/${accountId}/toggle-exclude`)
    return response.data
  }

  // Portfolio
  async getPortfolio(uploadId: number, accountId?: number): Promise<Portfolio> {
    const url = accountId
      ? `/portfolio/${uploadId}/account/${accountId}`
      : `/portfolio/${uploadId}`
    const response = await this.client.get<Portfolio>(url)
    return response.data
  }

  async getPositions(
    uploadId: number,
    params?: { account_id?: number; asset_category?: string; symbol?: string }
  ): Promise<{ data: OpenPosition[] }> {
    const response = await this.client.get(`/portfolio/${uploadId}/positions`, { params })
    return response.data
  }

  // Aggregate
  async getAggregate(uploadId: number): Promise<Aggregate> {
    const response = await this.client.get<Aggregate>(`/aggregate/${uploadId}`)
    return response.data
  }

  async getLatestAggregate(): Promise<Aggregate> {
    const response = await this.client.get<Aggregate>('/aggregate/latest')
    return response.data
  }

  // Metrics
  async getMetrics(uploadId: number): Promise<Metrics> {
    const response = await this.client.get<Metrics>(`/metrics/${uploadId}`)
    return response.data
  }

  async getLatestMetrics(): Promise<Metrics> {
    const response = await this.client.get<Metrics>('/metrics/latest')
    return response.data
  }

  // FX Rates
  async getFxRates(baseCurrency = 'USD'): Promise<FxRateSummary[]> {
    const response = await this.client.get<FxRateSummary[]>('/fx-rates/latest', {
      params: { base_currency: baseCurrency },
    })
    return response.data
  }

  async convertCurrency(
    amount: number,
    fromCurrency: string,
    toCurrency: string
  ): Promise<{ amount: number; from_currency: string; to_currency: string; result: number }> {
    const response = await this.client.get('/fx-rates/convert', {
      params: { amount, from_currency: fromCurrency, to_currency: toCurrency },
    })
    return response.data
  }

  async refreshFxRates(baseCurrency = 'USD'): Promise<{ message: string }> {
    const response = await this.client.post('/fx-rates/refresh', null, {
      params: { base_currency: baseCurrency },
    })
    return response.data
  }

  // Options
  async getOptions(params?: { underlying?: string; expired?: boolean }): Promise<{ options: Option[]; total: number }> {
    const response = await this.client.get('/options', { params })
    return response.data
  }

  async getOption(occSymbol: string): Promise<Option> {
    const response = await this.client.get<Option>(`/options/${encodeURIComponent(occSymbol)}`)
    return response.data
  }

  async updateOption(occSymbol: string, data: { dividend_yield?: number }): Promise<Option> {
    const response = await this.client.patch<Option>(`/options/${encodeURIComponent(occSymbol)}`, data)
    return response.data
  }

  async refreshOptions(uploadId: number): Promise<OptionRefreshResult> {
    const response = await this.client.post<OptionRefreshResult>(`/options/refresh/${uploadId}`)
    return response.data
  }

  async getUnderlyings(): Promise<{ underlyings: string[] }> {
    const response = await this.client.get('/options/underlyings/list')
    return response.data
  }

  // Benchmark Rates
  async getBenchmarkRates(): Promise<{ rates: BenchmarkRate[] }> {
    const response = await this.client.get('/benchmark-rates')
    return response.data
  }

  async getBenchmarkRateHistory(params?: { duration?: string; limit?: number }): Promise<{ rates: BenchmarkRate[] }> {
    const response = await this.client.get('/benchmark-rates/history', { params })
    return response.data
  }

  async createBenchmarkRate(data: { duration: string; rate: number; rate_date: string; source?: string }): Promise<BenchmarkRate> {
    const response = await this.client.post<BenchmarkRate>('/benchmark-rates', data)
    return response.data
  }

  async refreshBenchmarkRates(apiKey?: string): Promise<{ rates_updated: number }> {
    const response = await this.client.post('/benchmark-rates/refresh', null, {
      params: apiKey ? { api_key: apiKey } : undefined,
    })
    return response.data
  }

  async deleteBenchmarkRate(rateId: number): Promise<void> {
    await this.client.delete(`/benchmark-rates/${rateId}`)
  }

  // Subsectors
  async getSubsectors(): Promise<{ subsectors: Subsector[]; total: number }> {
    const response = await this.client.get('/subsectors')
    return response.data
  }

  async getSubsector(subsectorId: number): Promise<Subsector> {
    const response = await this.client.get<Subsector>(`/subsectors/${subsectorId}`)
    return response.data
  }

  async createSubsector(name: string): Promise<Subsector> {
    const response = await this.client.post<Subsector>('/subsectors', { name })
    return response.data
  }

  async updateSubsector(subsectorId: number, name: string): Promise<Subsector> {
    const response = await this.client.patch<Subsector>(`/subsectors/${subsectorId}`, { name })
    return response.data
  }

  async deleteSubsector(subsectorId: number): Promise<void> {
    await this.client.delete(`/subsectors/${subsectorId}`)
  }

  async addUnderlyingToSubsector(subsectorId: number, underlying: string): Promise<Subsector> {
    const response = await this.client.post<Subsector>(`/subsectors/${subsectorId}/underlyings`, { underlying })
    return response.data
  }

  async removeUnderlyingFromSubsector(subsectorId: number, underlying: string): Promise<void> {
    await this.client.delete(`/subsectors/${subsectorId}/underlyings/${encodeURIComponent(underlying)}`)
  }
}

export const api = new ApiClient()
export default api
